import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

const SECTIONS = [
  { id: "div1", label: "Div 1", icon: "fa-trophy", accent: "from-red-500 to-rose-600" },
  { id: "div2", label: "Div 2", icon: "fa-medal", accent: "from-orange-500 to-amber-600" },
  { id: "div3", label: "Div 3", icon: "fa-layer-group", accent: "from-emerald-500 to-teal-600" },
  { id: "div4", label: "Div 4", icon: "fa-seedling", accent: "from-cyan-500 to-blue-600" },
  { id: "edu", label: "Edu", icon: "fa-graduation-cap", accent: "from-violet-500 to-purple-600" },
];

const normalizeContestName = (name = "") => name.toLowerCase().replace(/\s+/g, " ");

const getContestSection = (contestName = "") => {
  const name = normalizeContestName(contestName);

  if (name.includes("educational")) return "edu";
  if (/(^|[^a-z0-9])div\.?\s*4([^a-z0-9]|$)/.test(name)) return "div4";
  if (/(^|[^a-z0-9])div\.?\s*3([^a-z0-9]|$)/.test(name)) return "div3";
  if (/(^|[^a-z0-9])div\.?\s*2([^a-z0-9]|$)/.test(name)) return "div2";
  if (/(^|[^a-z0-9])div\.?\s*1([^a-z0-9]|$)/.test(name)) return "div1";

  return null;
};

function ContestTracker({
  username,
  problemsWithStats,
  userSubmissions,
  userSubmissionsLoading,
  userSubmissionsError,
  onUsernameChange,
  toggleDarkMode,
  isDarkMode,
}) {
  const navigate = useNavigate();
  const [contests, setContests] = useState([]);
  const [contestLoading, setContestLoading] = useState(true);
  const [contestError, setContestError] = useState(null);
  const [activeSection, setActiveSection] = useState("div2");
  const [searchQuery, setSearchQuery] = useState("");
  const [progressFilter, setProgressFilter] = useState("all");

  useEffect(() => {
    const loadContests = async () => {
      try {
        setContestLoading(true);
        setContestError(null);

        const cachedContests = localStorage.getItem("codeforcesContests");
        const cachedTimestamp = localStorage.getItem("codeforcesContestsTimestamp");
        const oneHour = 60 * 60 * 1000;

        if (cachedContests && cachedTimestamp && Date.now() - parseInt(cachedTimestamp) < oneHour) {
          setContests(JSON.parse(cachedContests));
          setContestLoading(false);
          return;
        }

        const response = await fetch("https://codeforces.com/api/contest.list");
        const data = await response.json();

        if (data.status !== "OK") {
          throw new Error(data.comment || "Failed to fetch contests");
        }

        const finishedContests = data.result.filter((contest) => contest.phase === "FINISHED");
        setContests(finishedContests);
        localStorage.setItem("codeforcesContests", JSON.stringify(finishedContests));
        localStorage.setItem("codeforcesContestsTimestamp", Date.now().toString());
      } catch (error) {
        setContestError(error.message);
      } finally {
        setContestLoading(false);
      }
    };

    loadContests();
  }, []);

  const trackerData = useMemo(() => {
    const solvedByContest = new Map();
    const attemptedByContest = new Map();

    userSubmissions.forEach((submission) => {
      if (!submission.problem?.contestId) return;

      const key = `${submission.problem.contestId}-${submission.problem.index}`;
      const contestId = submission.problem.contestId;

      if (!attemptedByContest.has(contestId)) attemptedByContest.set(contestId, new Set());
      attemptedByContest.get(contestId).add(key);

      if (submission.verdict === "OK") {
        if (!solvedByContest.has(contestId)) solvedByContest.set(contestId, new Map());
        solvedByContest.get(contestId).set(key, {
          contestId,
          index: submission.problem.index,
          name: submission.problem.name,
          rating: submission.problem.rating,
        });
      }
    });

    const problemsByContest = new Map();
    problemsWithStats.forEach((problem) => {
      if (!problem.contestId) return;
      if (!problemsByContest.has(problem.contestId)) problemsByContest.set(problem.contestId, []);
      problemsByContest.get(problem.contestId).push(problem);
    });

    const grouped = SECTIONS.reduce((acc, section) => {
      acc[section.id] = [];
      return acc;
    }, {});

    contests.forEach((contest) => {
      const section = getContestSection(contest.name);
      if (!section || !problemsByContest.has(contest.id)) return;

      const contestProblems = problemsByContest.get(contest.id).sort((a, b) => a.index.localeCompare(b.index, undefined, { numeric: true }));
      const solvedProblems = Array.from(solvedByContest.get(contest.id)?.values() || []).sort((a, b) => a.index.localeCompare(b.index, undefined, { numeric: true }));
      const attemptedCount = attemptedByContest.get(contest.id)?.size || 0;

      grouped[section].push({
        ...contest,
        totalProblems: contestProblems.length,
        solvedProblems,
        solvedCount: solvedProblems.length,
        attemptedCount,
      });
    });

    Object.keys(grouped).forEach((section) => {
      grouped[section].sort((a, b) => b.id - a.id);
    });

    return grouped;
  }, [contests, problemsWithStats, userSubmissions]);

  const sectionTotals = useMemo(() => {
    return SECTIONS.reduce((acc, section) => {
      const contestsInSection = trackerData[section.id] || [];
      const solved = contestsInSection.reduce((sum, contest) => sum + contest.solvedCount, 0);
      const total = contestsInSection.reduce((sum, contest) => sum + contest.totalProblems, 0);
      const touched = contestsInSection.filter((contest) => contest.solvedCount > 0 || contest.attemptedCount > 0).length;

      acc[section.id] = {
        contests: contestsInSection.length,
        touched,
        solved,
        total,
      };
      return acc;
    }, {});
  }, [trackerData]);

  const visibleContests = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();

    return (trackerData[activeSection] || []).filter((contest) => {
      const isTouched = contest.solvedCount > 0 || contest.attemptedCount > 0;
      if (progressFilter === "touched" && !isTouched) return false;
      if (progressFilter === "untouched" && isTouched) return false;
      if (!query) return true;

      return contest.name.toLowerCase().includes(query) || contest.id.toString().includes(query);
    });
  }, [activeSection, searchQuery, progressFilter, trackerData]);

  const activeMeta = SECTIONS.find((section) => section.id === activeSection);
  const activeTotals = sectionTotals[activeSection] || { contests: 0, touched: 0, solved: 0, total: 0 };

  const refreshContests = () => {
    localStorage.removeItem("codeforcesContests");
    localStorage.removeItem("codeforcesContestsTimestamp");
    window.location.reload();
  };

  return (
    <div
      className={`min-h-screen font-['Inter'] transition-all duration-500 ${
        isDarkMode ? "bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-slate-100" : "bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/50"
      }`}
    >
      <header className={`backdrop-blur-xl border-b transition-all duration-500 ${isDarkMode ? "bg-slate-900/80 border-slate-700/50" : "bg-white/80 border-slate-200/50"}`}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex flex-col gap-5 lg:flex-row lg:justify-between lg:items-center py-8">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate("/")}
                className={`p-3 rounded-2xl transition-all duration-300 hover:scale-110 ${
                  isDarkMode
                    ? "bg-gradient-to-br from-slate-700 to-slate-800 hover:from-slate-600 hover:to-slate-700 text-slate-300"
                    : "bg-gradient-to-br from-slate-100 to-slate-200 hover:from-slate-200 hover:to-slate-300 text-slate-600"
                }`}
                title="Back to Home"
              >
                <i className="fas fa-arrow-left text-xl"></i>
              </button>
              <div className={`p-3 rounded-2xl bg-gradient-to-br ${activeMeta.accent}`}>
                <i className={`fas ${activeMeta.icon} text-white text-xl`}></i>
              </div>
              <div>
                <h1 className={`text-3xl font-bold bg-gradient-to-r ${isDarkMode ? "from-slate-100 to-slate-300" : "from-slate-900 to-slate-700"} bg-clip-text text-transparent`}>
                  Contest Tracker
                </h1>
                <p className={`text-sm mt-1 ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>Divisional and Educational round progress for {username}</p>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <button
                onClick={() => navigate("/problems")}
                className={`px-4 py-2 rounded-xl transition-all duration-300 hover:scale-105 font-medium ${
                  isDarkMode ? "bg-slate-800/70 text-slate-200 hover:bg-slate-700/70" : "bg-slate-100/80 text-slate-700 hover:bg-slate-200/80"
                }`}
              >
                <i className="fas fa-search mr-2"></i>
                Problems
              </button>
              <button
                onClick={() => navigate("/stats")}
                className="px-4 py-2 rounded-xl transition-all duration-300 hover:scale-105 font-medium bg-gradient-to-r from-green-600 to-emerald-600 text-white"
              >
                <i className="fas fa-chart-bar mr-2"></i>
                Stats
              </button>
              <button
                onClick={toggleDarkMode}
                className={`p-3 rounded-xl transition-all duration-300 hover:scale-105 ${
                  isDarkMode ? "bg-slate-800/50 hover:bg-slate-700/50 text-slate-300" : "bg-slate-100/80 hover:bg-slate-200/80 text-slate-600"
                }`}
                title="Toggle dark mode"
              >
                <i className={`fas ${isDarkMode ? "fa-sun" : "fa-moon"}`}></i>
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-8 py-8">
        {(contestError || userSubmissionsError) && (
          <div className={`mb-6 p-4 rounded-2xl ${isDarkMode ? "bg-red-500/10 text-red-300 border border-red-500/20" : "bg-red-50 text-red-700 border border-red-200"}`}>
            <i className="fas fa-exclamation-circle mr-2"></i>
            {contestError || userSubmissionsError}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-8">
          {SECTIONS.map((section) => {
            const totals = sectionTotals[section.id] || { contests: 0, touched: 0, solved: 0, total: 0 };
            const isActive = activeSection === section.id;

            return (
              <button
                key={section.id}
                onClick={() => setActiveSection(section.id)}
                className={`text-left rounded-2xl p-5 border transition-all duration-300 hover:scale-[1.02] ${
                  isActive
                    ? `bg-gradient-to-br ${section.accent} text-white border-transparent shadow-xl`
                    : isDarkMode
                    ? "bg-slate-800/60 border-slate-700/50 text-slate-200 hover:bg-slate-800"
                    : "bg-white/80 border-slate-200/50 text-slate-800 hover:bg-white"
                }`}
              >
                <div className="flex items-center justify-between mb-4">
                  <span className="font-bold text-lg">{section.label}</span>
                  <i className={`fas ${section.icon}`}></i>
                </div>
                <div className="text-2xl font-bold">{totals.solved}</div>
                <div className={`text-sm ${isActive ? "text-white/80" : isDarkMode ? "text-slate-400" : "text-slate-500"}`}>
                  solved problems
                </div>
                <div className={`text-xs mt-3 ${isActive ? "text-white/75" : isDarkMode ? "text-slate-500" : "text-slate-400"}`}>
                  {totals.touched}/{totals.contests} contests touched
                </div>
              </button>
            );
          })}
        </div>

        <div
          className={`rounded-3xl backdrop-blur-xl overflow-hidden mb-8 transition-all duration-500 ${
            isDarkMode ? "bg-slate-800/60 border border-slate-700/50 shadow-2xl" : "bg-white/80 border border-slate-200/50 shadow-xl"
          }`}
        >
          <div
            className={`px-8 py-6 border-b ${
              isDarkMode ? "border-slate-700/50 bg-gradient-to-r from-slate-800/80 to-slate-900/80" : "border-slate-200/50 bg-gradient-to-r from-slate-50/80 to-blue-50/80"
            }`}
          >
            <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <h2 className={`text-2xl font-bold ${isDarkMode ? "text-slate-100" : "text-slate-900"}`}>{activeMeta.label} Progress</h2>
                <p className={`text-sm mt-1 ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>
                  {activeTotals.solved}/{activeTotals.total} known problems solved across {activeTotals.contests} contests
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  placeholder="Search contest or ID"
                  className={`px-4 py-3 rounded-xl border transition-all duration-300 focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500 ${
                    isDarkMode ? "bg-slate-800/70 border-slate-600/50 text-slate-100 placeholder-slate-400" : "bg-white border-slate-200 text-slate-900 placeholder-slate-500"
                  }`}
                />
                <div className={`flex rounded-xl p-1 ${isDarkMode ? "bg-slate-900/60 border border-slate-700/50" : "bg-slate-100 border border-slate-200"}`}>
                  {[
                    { id: "all", label: "All", icon: "fa-list" },
                    { id: "touched", label: "Touched", icon: "fa-check-square" },
                    { id: "untouched", label: "Untouched", icon: "fa-square" },
                  ].map((filter) => (
                    <button
                      key={filter.id}
                      onClick={() => setProgressFilter(filter.id)}
                      className={`px-3 py-2 rounded-lg text-sm font-semibold transition-all duration-300 ${
                        progressFilter === filter.id
                          ? "bg-gradient-to-r from-blue-600 to-indigo-700 text-white shadow"
                          : isDarkMode
                          ? "text-slate-300 hover:bg-slate-800"
                          : "text-slate-600 hover:bg-white"
                      }`}
                    >
                      <i className={`fas ${filter.icon} mr-2`}></i>
                      {filter.label}
                    </button>
                  ))}
                </div>
                <button
                  onClick={refreshContests}
                  className={`px-4 py-3 rounded-xl font-medium transition-all duration-300 ${
                    isDarkMode ? "bg-slate-700/70 text-slate-200 hover:bg-slate-700" : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                  }`}
                >
                  <i className="fas fa-sync-alt mr-2"></i>
                  Refresh
                </button>
              </div>
            </div>
          </div>

          <div className="p-8">
            {(contestLoading || userSubmissionsLoading) && (
              <div className="py-16 text-center">
                <div className={`animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-5 ${isDarkMode ? "border-blue-400" : "border-blue-600"}`}></div>
                <p className={`font-medium ${isDarkMode ? "text-slate-300" : "text-slate-600"}`}>Loading tracker data...</p>
              </div>
            )}

            {!contestLoading && !userSubmissionsLoading && visibleContests.length === 0 && (
              <div className={`py-16 text-center rounded-2xl ${isDarkMode ? "bg-slate-900/40 text-slate-400" : "bg-slate-50 text-slate-500"}`}>
                <i className="fas fa-folder-open text-3xl mb-4"></i>
                <p className="font-medium">No contests match the current filters.</p>
              </div>
            )}

            {!contestLoading && !userSubmissionsLoading && visibleContests.length > 0 && (
              <div className="space-y-4">
                {visibleContests.map((contest) => {
                  const progress = contest.totalProblems > 0 ? Math.round((contest.solvedCount / contest.totalProblems) * 100) : 0;

                  return (
                    <div
                      key={contest.id}
                      className={`rounded-2xl border p-5 transition-all duration-300 ${
                        isDarkMode ? "bg-slate-900/40 border-slate-700/50 hover:bg-slate-900/70" : "bg-white border-slate-200/70 hover:shadow-lg"
                      }`}
                    >
                      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-3 mb-2">
                            <a
                              href={`https://codeforces.com/contest/${contest.id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={`font-bold text-lg hover:underline ${isDarkMode ? "text-slate-100" : "text-slate-900"}`}
                            >
                              {contest.name}
                            </a>
                            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${isDarkMode ? "bg-slate-800 text-slate-300" : "bg-slate-100 text-slate-600"}`}>#{contest.id}</span>
                          </div>
                          <div className={`text-sm ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>
                            {contest.solvedCount}/{contest.totalProblems} solved
                            {contest.attemptedCount > contest.solvedCount ? `, ${contest.attemptedCount - contest.solvedCount} unsolved attempted` : ""}
                          </div>
                        </div>

                        <div className="w-full lg:w-56">
                          <div className="flex justify-between text-sm font-semibold mb-2">
                            <span className={isDarkMode ? "text-slate-300" : "text-slate-700"}>Progress</span>
                            <span className={isDarkMode ? "text-slate-400" : "text-slate-500"}>{progress}%</span>
                          </div>
                          <div className={`h-3 rounded-full overflow-hidden ${isDarkMode ? "bg-slate-800" : "bg-slate-100"}`}>
                            <div className={`h-full rounded-full bg-gradient-to-r ${activeMeta.accent}`} style={{ width: `${progress}%` }}></div>
                          </div>
                        </div>
                      </div>

                      <div className="mt-5">
                        {contest.solvedProblems.length > 0 ? (
                          <div className="flex flex-wrap gap-2">
                            {contest.solvedProblems.map((problem) => (
                              <a
                                key={`${problem.contestId}-${problem.index}`}
                                href={`https://codeforces.com/problemset/problem/${problem.contestId}/${problem.index}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                title={problem.name}
                                className={`px-3 py-2 rounded-xl text-sm font-semibold transition-all duration-300 hover:scale-105 ${
                                  isDarkMode ? "bg-green-500/15 text-green-300 border border-green-500/20" : "bg-green-50 text-green-700 border border-green-200"
                                }`}
                              >
                                {problem.index}
                                <span className="font-normal ml-2">{problem.name}</span>
                              </a>
                            ))}
                          </div>
                        ) : (
                          <div className={`text-sm ${isDarkMode ? "text-slate-500" : "text-slate-400"}`}>No solved problems from this contest yet.</div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <div className={`text-sm rounded-2xl p-5 ${isDarkMode ? "bg-slate-800/50 text-slate-400 border border-slate-700/50" : "bg-white/70 text-slate-500 border border-slate-200/50"}`}>
          <i className="fas fa-info-circle mr-2"></i>
          Contest categories are inferred from official Codeforces contest names. Solved counts use accepted submissions cached for {username}; change handle from the stats page if needed.
          <button onClick={() => onUsernameChange("")} className={`ml-2 font-semibold ${isDarkMode ? "text-blue-300" : "text-blue-600"}`}>
            Switch user
          </button>
        </div>
      </main>
    </div>
  );
}

export default ContestTracker;
